import {useCallback, useMemo, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {toClassroomIds} from 'client/TeacherDashboard/SkillPaths/hooks/skillPathEntryFilters';
import type {
  SkillPathSortValue,
  SkillPathStatusSegment
} from 'client/TeacherDashboard/SkillPaths/types';
import type {SortValue} from 'client/TeacherDashboard/types';
import {normalizeError} from 'lib/normalizeError';

import type {SharedDiagnosticFilters} from './diagnosticFilters';
import {buildDiagnosticFilter, buildDiagnosticSort, toStatusArg} from './diagnosticFilters';
import type {DiagnosticListFieldsFragment} from './queries/Diagnostics.queries.generated';
import {
  useDiagnosticStatusCountsQuery,
  useDiagnosticsListQuery
} from './queries/Diagnostics.queries.generated';

/** All the collapsed group renders. Every row costs scoped calculations, and most never expand. */
const COLLAPSED_ROWS = 3;

/** The read's own ceiling (Ash `max_page_size`); a larger `first` is rejected, not truncated. */
const MAX_PAGE_SIZE = 250;

/** Stable identity, so an empty page doesn't read as a new one to the memo below. */
const NO_DIAGNOSTICS: DiagnosticListFieldsFragment[] = [];

const NO_PAGES = {key: '', entries: NO_DIAGNOSTICS, cursor: null as string | null};

export interface SkillPathsDiagnosticsFilters extends SharedDiagnosticFilters {
  /** Off with the `adaptive_diagnostics` flag: nothing is fetched and every result is empty. */
  skip: boolean;
  segment: SkillPathStatusSegment;
  sort: SortValue<SkillPathSortValue>;
}

export interface SkillPathsDiagnosticsResult {
  diagnostics: DiagnosticListFieldsFragment[];
  /** Total matching the current status + filters, shown beside the group title. */
  total: number;
  counts: Record<SkillPathStatusSegment, number>;
  /** False until the counts land, so the shared control can hold its numbers back. */
  hasCounts: boolean;
  /** New totals are in flight — the ones on hand answer for the previous filters. */
  isCountsLoading: boolean;
  /** Every status is empty under the current filters — drives the onboarding state. */
  isEmptyInEveryStatus: boolean;
  isLoading: boolean;
  /** More rows match than are loaded, and another page can reach them. */
  hasMore: boolean;
  isLoadingMore: boolean;
  /** Load the next page of remaining rows. */
  showMore: () => void;
  hasError: boolean;
  refetch: () => void;
}

/**
 * The diagnostics group inside the Skill Paths view: the active status's rows plus the three
 * per-status totals the shared segmented control adds to the skill-path counts.
 */
export const useSkillPathsDiagnostics = (
  filters: SkillPathsDiagnosticsFilters
): SkillPathsDiagnosticsResult => {
  const {skip, segment, sort, ...shared} = filters;
  const {classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd} = shared;

  const sharedFilters = useMemo(
    () => ({classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd}),
    [classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd]
  );

  const listVariables = useMemo(
    () => ({
      classroomIds: toClassroomIds(classroomId),
      status: toStatusArg(segment),
      // Which date pair the row fragment asks for, plus the per-status ranges only All needs.
      isAllStatuses: segment === 'all',
      includeDueDates: segment !== 'scheduled',
      includeStartDates: segment === 'scheduled',
      filter: buildDiagnosticFilter(sharedFilters, segment),
      sort: buildDiagnosticSort(sort, segment, classroomId)
    }),
    [classroomId, segment, sort, sharedFilters]
  );

  const countsVariables = useMemo(
    () => ({
      allFilter: buildDiagnosticFilter(sharedFilters, 'all'),
      openFilter: buildDiagnosticFilter(sharedFilters, 'open'),
      closedFilter: buildDiagnosticFilter(sharedFilters, 'closed'),
      scheduledFilter: buildDiagnosticFilter(sharedFilters, 'scheduled')
    }),
    [sharedFilters]
  );

  // Separate from the list query so paging or re-sorting the visible status doesn't refetch the
  // other two totals.
  const {
    data: countsData,
    previousData: previousCountsData,
    loading: countsLoading,
    error: countsError,
    refetch: refetchCounts
  } = useDiagnosticStatusCountsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    variables: countsVariables
  });

  // Falls back to the previous totals mid-fetch, as the skill-path counts do, so the shared control
  // keeps its numbers rather than emptying them on every filter change.
  const settledCounts = countsData ?? previousCountsData;
  const all = settledCounts?.all?.count ?? 0;
  const open = settledCounts?.open?.count ?? 0;
  const closed = settledCounts?.closed?.count ?? 0;
  const scheduled = settledCounts?.scheduled?.count ?? 0;
  // Keyed on the numbers, not the response, so the control's labels only rebuild when a total moves.
  const counts = useMemo(() => ({all, open, closed, scheduled}), [all, open, closed, scheduled]);

  const {data, previousData, loading, error, refetch, fetchMore} = useDiagnosticsListQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    variables: {...listVariables, first: COLLAPSED_ROWS}
  });

  // Every page written here is keyed to the request that produced it, and reads ignore any key but
  // the current one, so a page landing after a filter, sort or refetch change is inert.
  const [refetchGen, setRefetchGen] = useState(0);
  const variablesKey = `${refetchGen}:${JSON.stringify(listVariables)}`;
  const [morePages, setMorePages] = useState(NO_PAGES);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleRefetch = useCallback(() => {
    // Bumping the generation retires accumulated pages so they can't outlive the rows they extend.
    setRefetchGen((generation) => generation + 1);
    // Both, or a counts-only failure leaves an error panel whose Try Again can never clear it.
    // Rejections are swallowed because the failures surface reactively via `error`.
    void refetch().catch(() => {});
    void refetchCounts().catch(() => {});
  }, [refetch, refetchCounts]);

  // A new result set collapses the group, rather than re-fetching every row for an ordering the
  // teacher hasn't looked at yet. The pages are discarded, not just hidden: leaving a filter and
  // returning produces the same key, and stale rows would come back with it — never revalidated,
  // since only the first page is watched.
  const [renderedKey, setRenderedKey] = useState(variablesKey);
  if (renderedKey !== variablesKey) {
    setRenderedKey(variablesKey);
    setMorePages(NO_PAGES);
    if (isLoadingMore) {
      setIsLoadingMore(false);
    }
  }

  const page = data?.diagnostics ?? previousData?.diagnostics;
  const firstPage = page?.results;
  const accumulated = morePages.key === variablesKey ? morePages : null;
  const diagnostics = useMemo(
    () => [...(firstPage ?? []), ...(accumulated?.entries ?? NO_DIAGNOSTICS)],
    [firstPage, accumulated]
  );
  // The counts query answers this for the same filter, so the list query no longer asks too. The
  // group is skeletoned until both have landed, so the rows and this number can't disagree on screen.
  const total = counts[segment];
  const cursor = accumulated?.cursor ?? page?.endKeyset ?? null;
  // Keyset, not offset: `first` is capped server-side, so reaching past it takes another page.
  const hasMore = diagnostics.length < total && Boolean(cursor);

  const showMore = useCallback(() => {
    // `data` rather than `loading`: the cursor belongs to the first page for THESE variables, and
    // that is what has to have landed. `loading` is also true while a warm cache revalidates, where
    // the rows — and the cursor — are already correct.
    if (!data?.diagnostics || isLoadingMore || !hasMore || !cursor) {
      return;
    }
    const requestedKey = variablesKey;
    setIsLoadingMore(true);
    fetchMore({
      variables: {
        ...listVariables,
        first: Math.min(total - diagnostics.length, MAX_PAGE_SIZE),
        after: cursor
      }
    })
      .then((result) => {
        const next = result.data?.diagnostics;
        setMorePages((current) => {
          const base = current.key === requestedKey ? current.entries : NO_DIAGNOSTICS;
          // De-duplicated: an edit between pages shifts the keyset and can repeat a row.
          const seen = new Set([...(firstPage ?? []), ...base].map((d) => d.id));
          return {
            key: requestedKey,
            entries: [...base, ...(next?.results ?? []).filter((d) => !seen.has(d.id))],
            cursor: next?.endKeyset ?? null
          };
        });
      })
      .catch((fetchMoreError) => {
        // Toast rather than blank the group — the rows already on screen are still good.
        addToast({
          color: 'negative',
          title: 'Couldn’t load more diagnostics',
          message: normalizeError(fetchMoreError).message
        });
      })
      .finally(() => setIsLoadingMore(false));
  }, [
    cursor,
    data,
    diagnostics.length,
    fetchMore,
    firstPage,
    hasMore,
    isLoadingMore,
    listVariables,
    total,
    variablesKey
  ]);

  return {
    diagnostics,
    total,
    counts,
    hasCounts: Boolean(settledCounts),
    isCountsLoading: countsLoading && !countsData,
    isEmptyInEveryStatus: Boolean(settledCounts) && !countsError && all === 0,
    // Includes the totals, which decide the empty state — painting first risks swapping the section
    // out a moment later. Paging is excluded: those rows stay put while the next page loads.
    isLoading: (loading && !data) || (countsLoading && !countsData),
    hasMore,
    isLoadingMore,
    showMore,
    // Includes the counts query: a silent failure there would report every status tab as 0.
    hasError: Boolean(error) || Boolean(countsError),
    refetch: handleRefetch
  };
};
